import React, {useState, useEffect, useCallback} from 'react';
import {
  Box,
  VStack,
  Text,
  useTheme,
  Spinner,
  Center,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalBody,
  HStack,
  useDisclosure,
} from '@chakra-ui/react';
import {useSearchParams, useNavigate} from 'react-router-dom';
import MeetUpCard from '../../components/Articles/MeetUpCard';
import EventCard from '../../components/Articles/EventCard';
import OfferCard from '../../components/Articles/Offers/OfferCard';
import ArticleModal from '../../components/Articles/Modal';
import Icon from '../../components/Icon';
import {TextG10, TextG12, TextG14, TextG16, TextG20, TextG24} from '../../components/typography';
import BookmarkButton from '../../components/BookmarkButton';
import {CustomButton} from '../../components/button';
import ChallengeBanner from '../../components/ChallengeBanner';
import {useBookmarkFeedState} from '../../hooks/useBookmarkFeedState';
import {useBookmarkSessionTracking} from '../../hooks/useBookmarkSessionTracking';
import {useBookmarkActions} from '../../hooks/useBookmarkActions';

const AMBASSADOR_PRESENT_TOPIC = process.env.NODE_ENV === 'production' ? 336 : 223;

// Session storage key for tracking initial bookmarks in onboarding
const INTRO_BOOKMARKS_INITIAL_KEY = 'intro_bookmarks_initial_ids_v2';

interface IntroBookmarksProps {
  onSessionBookmarkCountChange?: (count: number) => void;
  showExitConfirmation?: boolean;
  onExitConfirmationClose?: () => void;
  onExitConfirmationSkip?: () => void;
  // When true, the welcome modal ("Let's start making local plans") is hidden so a
  // higher-level bridge modal can stand in for it (places → events handoff).
  suppressWelcomeModal?: boolean;
}

const IntroBookmarks = ({
  onSessionBookmarkCountChange,
  showExitConfirmation = false,
  onExitConfirmationClose,
  onExitConfirmationSkip,
  suppressWelcomeModal = false,
}: IntroBookmarksProps) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const {isOpen, onOpen, onClose} = useDisclosure();
  const [showHeaderAnimation, setShowHeaderAnimation] = useState(false);
  const [headerBookmarked, setHeaderBookmarked] = useState(false);
  const [showModal, setShowModal] = useState(!suppressWelcomeModal);

  // Shared bookmark hooks
  const {
    hyperLocal,
    systemPopular,
    generalUpcoming,
    bookmarkedUpcoming,
    bookmarkedIds,
    feedData,
    loading,
    bannerRef,
    bookmarkedUpcomingSectionRef,
    bannerHeight,
    apiBookmarkedUpcomingCount,
  } = useBookmarkFeedState();

  const {initialBookmarkedIds, sessionBookmarkedIds, setSessionBookmarkedIds} =
    useBookmarkSessionTracking(INTRO_BOOKMARKS_INITIAL_KEY, feedData);

  const {handleBookmark} = useBookmarkActions({
    bookmarkedIds,
    initialBookmarkedIds,
    setSessionBookmarkedIds,
  });

  // Banner count uses only the cache's meta count (optimistically adjusted on every toggle).
  // Session bookmarks stay for exit-confirmation / summary but are NOT added here to avoid
  // double-counting once refetch aligns the backend count with the optimistic value.
  const bookmarkedCount = apiBookmarkedUpcomingCount;

  // Notify parent of session bookmark count changes
  useEffect(() => {
    onSessionBookmarkCountChange?.(sessionBookmarkedIds.size);
  }, [sessionBookmarkedIds.size, onSessionBookmarkCountChange]);

  useEffect(() => {
    if (searchParams.has('id')) {
      onOpen();
    }
  }, [searchParams.get('id'), onOpen]);

  const renderCard = (article: any, section: string) => {
    const isBookmarked = bookmarkedIds.has(article.id.toString());

    // Determine card component based on type field from API
    let CardComponent;
    let cardProps;

    if (article.type === 'offer') {
      CardComponent = OfferCard;
      // Suppress OfferCard's internal bookmark element — the onboarding page overlays its own
      cardProps = {offer: article, showBookmark: false};
    } else if (article.type === 'meetup' || article.is_meetup) {
      CardComponent = MeetUpCard;
      cardProps = {'meet-up': article};
    } else {
      CardComponent = EventCard;
      cardProps = {event: article};
    }

    const handleCardClick = (e?: any) => {
      if (e) {
        e.preventDefault();
        e.stopPropagation();
      }
      const params = new URLSearchParams(searchParams);
      params.set('id', article.id.toString());
      params.set(
        'articleType',
        article.type === 'meetup' || article.is_meetup ? 'meet-up' : article.type,
      );
      setSearchParams(params);
    };

    // Check if this article has the ambassador topic
    const hasAmbassadorTopic = article?.topics_of_interest?.some(
      (topic: any) => topic.id === AMBASSADOR_PRESENT_TOPIC,
    );

    return (
      <Box key={`${section}-${article.id}`} position="relative">
        <CardComponent
          {...cardProps}
          onClick={handleCardClick}
          forcePremium
          hideInterestToggle={true}
        />
        <BookmarkButton
          isBookmarked={isBookmarked}
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            handleBookmark(article);
          }}
          position="absolute"
          top={hasAmbassadorTopic ? '46px' : '12px'}
          right="12px"
          size="36px"
          iconSize="14px"
          // OfferCard wraps its output in a z-index:10 container; overlay must render above it.
          zIndex={20}
        />
      </Box>
    );
  };

  const loadType = searchParams.has('articleType')
    ? searchParams.get('articleType')?.toLowerCase()
    : 'event';

  return (
    <>
      {/* Exit Confirmation Modal */}
      <Modal isOpen={showExitConfirmation} onClose={() => onExitConfirmationClose?.()} isCentered>
        <ModalOverlay />
        <ModalContent w="90%" maxW="90vw" borderRadius="12px" p={0}>
          <ModalBody p={6} textAlign="center">
            <VStack spacing={4}>
              <Text fontSize="40px">🔖</Text>
              <TextG24 fontSize="26px" fontWeight="600" color="#262626">
                Save at least 3 things
              </TextG24>
              <TextG16 color="#262626" lineHeight="1.5">
                You will always want{' '}
                <Text as="span" fontWeight="700">
                  at least 3 things
                </Text>{' '}
                saved in the future so Walkabout can pair you with interested neighbors.
              </TextG16>
              <CustomButton
                background="#C9136B"
                color="white"
                w="100%"
                h="44px"
                borderRadius="100px"
                boxShadow="0 -4px 0 0 rgba(0, 0, 0, 0.25) inset"
                onClick={() => onExitConfirmationClose?.()}
                mt={2}>
                <TextG14 fontWeight="600" color="white">
                  Keep Saving
                </TextG14>
              </CustomButton>
              <Text
                fontSize="14px"
                color="#757D84"
                cursor="pointer"
                onClick={() => onExitConfirmationSkip?.()}
                textDecoration="underline">
                Skip For Now
              </Text>
            </VStack>
          </ModalBody>
        </ModalContent>
      </Modal>

      {/* Article Detail Modal */}
      {searchParams.has('id') && (
        <ArticleModal
          open={isOpen}
          close={() => {
            searchParams.delete('id');
            searchParams.delete('articleType');
            setSearchParams(searchParams);
            onClose();
          }}
          type={loadType ?? 'event'}
        />
      )}

      {/* Welcome Modal */}
      <Modal isOpen={showModal} onClose={() => setShowModal(false)} isCentered size="sm">
        <ModalOverlay bg="rgba(0, 0, 0, 0.4)" />
        <ModalContent mx={4} borderRadius="24px" py={6} px={4}>
          <ModalBody p={0}>
            <VStack spacing={6} align="stretch">
              {/* Header */}
              <VStack spacing={2}>
                <TextG24 fontSize="26px" fontWeight="700" textAlign="center" lineHeight="34px">
                  {`Let's start making `}
                </TextG24>
                <TextG24
                  fontSize="26px"
                  fontWeight="700"
                  color="#C9136B"
                  textAlign="center"
                  lineHeight="34px">
                  local plans
                </TextG24>
              </VStack>

              {/* Numbered List */}
              <VStack spacing={4} align="stretch">
                <HStack align="center" spacing={4}>
                  <Box
                    w="40px"
                    h="40px"
                    borderRadius="full"
                    bg="rgba(183, 28, 99, 0.1)"
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    flexShrink={0}>
                    <TextG20 lineHeight="24px" fontWeight="700" color="#C9136B">
                      1
                    </TextG20>
                  </Box>
                  <TextG16 lineHeight="24px" pt={1} fontWeight="500">
                    Browse local happenings to see what's around you.
                  </TextG16>
                </HStack>

                <HStack align="center" spacing={4}>
                  <Box
                    w="40px"
                    h="40px"
                    borderRadius="full"
                    bg="rgba(183, 28, 99, 0.1)"
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    flexShrink={0}>
                    <TextG20 lineHeight="24px" fontWeight="700" color="#C9136B">
                      2
                    </TextG20>
                  </Box>
                  <TextG16 lineHeight="24px" pt={1} fontWeight="500">
                    Save anything that looks even remotely interesting.
                  </TextG16>
                </HStack>

                <HStack align="center" spacing={4}>
                  <Box
                    w="40px"
                    h="40px"
                    borderRadius="full"
                    bg="rgba(183, 28, 99, 0.1)"
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    flexShrink={0}>
                    <TextG20 lineHeight="24px" fontWeight="700" color="#C9136B">
                      3
                    </TextG20>
                  </Box>
                  <TextG16 lineHeight="24px" pt={1} fontWeight="500">
                    See who's going and get notified as plans lock in.
                  </TextG16>
                </HStack>
              </VStack>

              {/* Bonus Tip */}
              <Box position="relative" pt={3} mt="54px !important">
                <Box
                  position="absolute"
                  top="-20px"
                  left="16px"
                  bg="#FAE7F0"
                  borderTopRadius="8px"
                  px="12px"
                  py="6px"
                  zIndex={1}>
                  <TextG12 fontWeight="700" color="#C9136B">
                    Bonus Tip
                  </TextG12>
                </Box>
                <Box bg="#F8F6F7" borderRadius="8px" p="16px" border="2px solid #F4CFE2">
                  <TextG14 fontWeight="500" lineHeight="22px" color="#363636">
                    Pick at least one event with Community Connector to meet your first connection.
                  </TextG14>
                </Box>
              </Box>

              {/* Got It Button */}
              <Box
                mt="24px !important"
                as="button"
                bg="#C9136B"
                color="white"
                py={4}
                borderRadius="full"
                onClick={() => {
                  setShowModal(false);
                  // Trigger header animation 1.5 seconds after modal closes
                  setTimeout(() => {
                    setHeaderBookmarked(true);
                    setShowHeaderAnimation(true);
                  }, 1500);
                }}
                _active={{transform: 'scale(0.98)'}}
                transition="all 0.2s">
                <TextG16 fontWeight="500">See What's Interesting Nearby</TextG16>
              </Box>
            </VStack>
          </ModalBody>
        </ModalContent>
      </Modal>

      {/* Main Content */}
      <VStack
        flex={1}
        spacing={4}
        px={4}
        pt={4}
        pb={4}
        align="stretch"
        justify="flex-start"
        overflow="auto">
        {/* Title */}
        <Box mb="24px !important">
          <HStack spacing={2} align="center" mb={1}>
            <TextG24 fontSize="26px" fontWeight="700" lineHeight="34px" color="#C9136B">
              Save activities you might like.{' '}
            </TextG24>
          </HStack>
          <TextG24 fontSize="26px" lineHeight="34px" color="#262626" fontWeight="700">
            See who's planning to go
          </TextG24>
        </Box>

        {/* Challenge Banner */}
        <ChallengeBanner />

        {/* Saved Items Banner — scroll-anchor to the bookmarked_upcoming section */}
        {bookmarkedCount > 0 && (
          <Box
            onClick={() => {
              bookmarkedUpcomingSectionRef.current?.scrollIntoView({
                behavior: 'smooth',
                block: 'start',
              });
            }}
            cursor="pointer"
            bg="#FFF0F6"
            border="1px solid #F4CFE2"
            borderRadius="0"
            p={3}
            mb={0}
            position="sticky"
            top={0}
            zIndex={101}
            ref={bannerRef}>
            <HStack justify="space-between" align="center">
              <HStack spacing={2}>
                <Icon
                  iconName="fi fi-sr-bookmark"
                  style={{fontSize: '14px', color: '#C9136B'}}
                />
                <TextG14 fontWeight="600" color="#C9136B">
                  {`${bookmarkedCount} thing${bookmarkedCount !== 1 ? 's' : ''} saved for the future`}
                </TextG14>
              </HStack>
              <Icon
                iconName="fi fi-rr-angle-down"
                style={{fontSize: '12px', color: '#C9136B'}}
              />
            </HStack>
          </Box>
        )}

        {/* Cards Feed */}
        {loading ? (
          <Center py={8}>
            <Spinner size="lg" color="#C9136B" />
          </Center>
        ) : (
          <VStack spacing={0} align="stretch">
            {hyperLocal.length > 0 && (
              <Box>
                <Box
                  position="sticky"
                  top={bookmarkedCount > 0 ? `${bannerHeight}px` : '-20px'}
                  bg="white"
                  zIndex={100}
                  py={3}
                  px={4}
                  mx={-4}
                  borderBottom="1px solid"
                  borderColor="gray.200">
                  <TextG16 fontWeight="700" color="#262626">
                    📍 Hyper-Local
                  </TextG16>
                  <TextG12 color={theme.colors.grey} mt={1}>
                    Close to you
                  </TextG12>
                </Box>
                <VStack spacing={4} align="stretch" pt={4}>
                  {hyperLocal.map((article) => renderCard(article, 'hyperLocal'))}
                </VStack>
              </Box>
            )}

            {systemPopular.length > 0 && (
              <Box mt={hyperLocal.length > 0 ? 6 : 0}>
                <Box
                  position="sticky"
                  top={bookmarkedCount > 0 ? `${bannerHeight}px` : '-20px'}
                  bg="white"
                  zIndex={100}
                  py={3}
                  px={4}
                  mx={-4}
                  borderBottom="1px solid"
                  borderColor="gray.200">
                  <TextG16 fontWeight="700" color="#262626">
                    🔥 Trending
                  </TextG16>
                  <TextG12 color={theme.colors.grey} mt={1}>
                    Popular with your neighbors
                  </TextG12>
                </Box>
                <VStack spacing={4} align="stretch" pt={4}>
                  {systemPopular.map((article) => renderCard(article, 'systemPopular'))}
                </VStack>
              </Box>
            )}

            {generalUpcoming.length > 0 && (
              <Box mt={hyperLocal.length > 0 || systemPopular.length > 0 ? 6 : 0}>
                <Box
                  position="sticky"
                  top={bookmarkedCount > 0 ? `${bannerHeight}px` : '-20px'}
                  bg="white"
                  zIndex={100}
                  py={3}
                  px={4}
                  mx={-4}
                  borderBottom="1px solid"
                  borderColor="gray.200">
                  <TextG16 fontWeight="700" color="#262626">
                    📅 Coming Up
                  </TextG16>
                  <TextG12 color={theme.colors.grey} mt={1}>
                    Events and offers
                  </TextG12>
                </Box>
                <VStack spacing={4} align="stretch" pt={4}>
                  {generalUpcoming.map((article) => renderCard(article, 'generalUpcoming'))}
                </VStack>
              </Box>
            )}

            {bookmarkedUpcoming.length > 0 && (
              <Box
                ref={bookmarkedUpcomingSectionRef}
                mt={
                  hyperLocal.length > 0 ||
                  systemPopular.length > 0 ||
                  generalUpcoming.length > 0
                    ? 6
                    : 0
                }>
                <Box
                  position="sticky"
                  top={bookmarkedCount > 0 ? `${bannerHeight}px` : '-20px'}
                  bg="white"
                  zIndex={100}
                  py={3}
                  px={4}
                  mx={-4}
                  borderBottom="1px solid"
                  borderColor="gray.200">
                  <TextG16 fontWeight="700" color="#262626">
                    🔖 Saved for the future
                  </TextG16>
                  <TextG12 color={theme.colors.grey} mt={1}>
                    Things you've saved
                  </TextG12>
                </Box>
                <VStack spacing={4} align="stretch" pt={4}>
                  {bookmarkedUpcoming.map((article) => renderCard(article, 'bookmarkedUpcoming'))}
                </VStack>
              </Box>
            )}

            {hyperLocal.length === 0 &&
              systemPopular.length === 0 &&
              generalUpcoming.length === 0 &&
              bookmarkedUpcoming.length === 0 && (
                <Center py={8}>
                  <TextG16 color={theme.colors.gray[600]}>No content available</TextG16>
                </Center>
              )}
          </VStack>
        )}
      </VStack>
    </>
  );
};

export default IntroBookmarks;
